import React, { useState, useEffect } from "react";
import PropTypes from "kf-proptypes/widgets";
import { Popover as AntPopover } from "antd";
import "antd/es/popover/style/css";

import { useTheme } from "base/themes";
import { useHideOnScroll } from "base/hooks";

import styles from "./popover.css";

/**
 * Renders a popover element with customizable content.
 */

export const POPOVER_POSITIONS = {
  TOP: "top",
  TOP_LEFT: "topLeft",
  TOP_RIGHT: "topRight",
  LEFT_TOP: "leftTop",
  LEFT: "left",
  LEFT_BOTTOM: "leftBottom",
  RIGHT_TOP: "rightTop",
  RIGHT: "right",
  RIGHT_BOTTOM: "rightBottom",
  BOTTOM_LEFT: "bottomLeft",
  BOTTOM: "bottom",
  BOTTOM_RIGHT: "bottomRight"
};

export const POPOVER_TRIGGERS = {
  HOVER: "hover",
  FOCUS: "focus",
  CLICK: "click",
  CONTEXT_MENU: "contextMenu"
};

export function Popover(props) {
  const {
    children,
    // Note : showArrow is not working with antd-4.9.0 and rc-tooltip-5.0.0
    // Don't waste your time in debugging, if you don't need arrow then try to override in css
    showArrow,
    targetRenderer,
    position,
    className,
    isOpen = false,
    trigger = [POPOVER_TRIGGERS.CLICK],
    getContainer = () => document.body,
    destroyOnClose = true,
    onOpen,
    align = {},
    arrowPointAtCenter = true,
    onClose,
    hideOnScroll = true,
    themeClassName
  } = props;
  const [isVisibile, setIsVisible] = useState(isOpen);
  useEffect(
    function visibleStateUpdatingEvent() {
      setIsVisible(isOpen);
    },
    [isOpen]
  );

  useHideOnScroll({
    hideOnScroll,
    className: "ant-popover",
    setState: () => setIsVisible(false)
  });

  const closePopup = () => {
    setIsVisible(false);
  };

  const getPosition = (position) => {
    const rtlPosition = {
      top: "top",
      topLeft: "topRight",
      topRight: "topLeft",
      leftTop: "rightTop",
      left: "right",
      leftBottom: "rightBottom",
      rightTop: "leftTop",
      right: "left",
      rightBottom: "leftBottom",
      bottomLeft: "bottomRight",
      bottom: "bottom",
      bottomRight: "bottomLeft"
    };
    return window.isRTL ? rtlPosition[position] : position;
  };
  let { themeClass } = useTheme();

  let themeClassToApply = themeClassName || themeClass;

  return (
    <AntPopover
      content={children}
      showArrow={showArrow}
      placement={getPosition(position)}
      trigger={trigger}
      visible={children && isVisibile}
      arrowPointAtCenter={arrowPointAtCenter}
      getPopupContainer={getContainer}
      onVisibleChange={(visible) => {
        setIsVisible(visible);
        if (visible) {
          onOpen && onOpen();
        } else {
          onClose && onClose();
        }
      }}
      overlayClassName={`${styles.popover} ${className} ${themeClassToApply}`}
      destroyTooltipOnHide={destroyOnClose}
      align={align}
    >
      {targetRenderer({ closePopup })}
    </AntPopover>
  );
}

Popover.propTypes = {
  /**
   * The content to be rendered inside the popover.
   */
  children: PropTypes.node.isRequired,
  /**
   * Specifies whether to show an arrow pointing to the target element.
   */
  showArrow: PropTypes.bool,
  /**
   * Specifies whether the popover is currently open.
   */
  isOpen: PropTypes.bool,
  /**
   * Function that returns the target element to which the popover is attached.
   */
  targetRenderer: PropTypes.func,
  /**
   * Position of the popover relative to the target element.
   */
  position: PropTypes.oneOf(Object.values(POPOVER_POSITIONS)),
  /**
   * Additional class name(s) for custom styling.
   */
  className: PropTypes.string,
  /**
   * Array of trigger events that open the popover (e.g., ['click', 'hover','focus]).
   */
  trigger: PropTypes.oneOf(Object.values(POPOVER_TRIGGERS)),
  /**
   * Function that returns the DOM container for the popover.
   */
  getContainer: PropTypes.func,
  /**
   * Additional class name(s) for the popover content.
   */
  popupClassName: PropTypes.string,
  /**
   * Specifies whether to destroy the popover content when it is closed.
   */
  destroyOnClose: PropTypes.bool,
  /**
   * Callback function invoked when the popover is opened.
   */
  onOpen: PropTypes.func,
  /**
   * Callback function invoked when the popover is closed.
   */
  onClose: PropTypes.func,
  /**
   * Specifies whether the arrow should point at the center of the target element.
   */
  arrowPointAtCenter: PropTypes.bool,
  /**
   * Custom alignment configuration for the popover.
   */
  align: PropTypes.shape({ offset: PropTypes.arrayOf(PropTypes.number) }),
  /**
   * Show/Hide popover on page scroll
   */
  hideOnScroll: PropTypes.bool,
  /**
   * Class name passed from parent to apply theme styles
   */
  themeClassName: PropTypes.string
};

Popover.defaultProps = {
  trigger: [POPOVER_TRIGGERS.CLICK],
  getContainer: () => document.body,
  destroyOnClose: true,
  align: {},
  arrowPointAtCenter: true
};
